import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { User, LoginData, RegisterData } from '../services/authService';



interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ user: User; access: string }>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  loading: boolean;
  isAuthenticated: boolean;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(authService.getAccessToken());
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setToken(authService.getAccessToken());
        } catch (error) {
          console.error('Auth check failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await authService.login({ email, password });
      setUser(response.user);
      setToken(response.access);
      // Retourner les données utilisateur et le token d'accès
      return { user: response.user, access: response.access };
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      setLoading(true);
      await authService.register(userData);
      // After successful registration, log the user in
      await login(userData.email, userData.password);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setToken(null);
  };

  const getToken = (): string | null => {
    return authService.getAccessToken();
  };

  const updateUser = async (userData: Partial<User>) => {
    if (!user) return;
    
    try {
      // Mise à jour optimiste de l'interface utilisateur
      setUser(prevUser => prevUser ? { ...prevUser, ...userData } : null);
      
      // Synchronisation avec le backend
      const updatedUser = await authService.updateProfile(userData);
      
      // Mise à jour avec les données du serveur
      setUser(prevUser => prevUser ? { ...prevUser, ...updatedUser } : null);
      
      return updatedUser;
    } catch (error) {
      // En cas d'erreur, on peut éventuellement restaurer l'état précédent
      // ou laisser l'interface dans un état incohérent avec un message d'erreur
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error; // Propager l'erreur pour la gérer dans le composant
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!user,
    getToken: () => token
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
